/**
 * Error Reporting Service
 *
 * Reports client-side errors to the backend telemetry endpoint.
 * Silently fails to avoid creating error loops.
 */

import { logger } from "./logger";

export interface ClientErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  errorType: "react_boundary" | "global_error" | "unhandled_rejection";
  metadata?: Record<string, unknown>;
}

/**
 * Report an error to the backend telemetry endpoint.
 *
 * @param error - The error details to report
 * @returns Promise that resolves when report is sent (or fails silently)
 */
export async function reportErrorToBackend(
  error: ClientErrorReport,
): Promise<void> {
  try {
    const response = await fetch("/api/telemetry/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(error),
    });

    if (!response.ok) {
      // Log but don't throw - we don't want error reporting to cause more errors
      logger.warn("error_report_response_not_ok", { status: response.status });
    }
  } catch {
    // Silently fail - don't create error loops
    logger.warn("error_report_failed");
  }
}

/**
 * Create an error report from an Error object with React component stack.
 *
 * @param error - The Error object
 * @param componentStack - React component stack trace
 * @returns Formatted error report
 */
export function createReactErrorReport(
  error: Error,
  componentStack?: string,
): ClientErrorReport {
  return {
    message: error.message,
    stack: error.stack,
    componentStack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    errorType: "react_boundary",
  };
}

/**
 * Create an error report from a global error event.
 *
 * @param message - Error message
 * @param source - Source file URL
 * @param lineno - Line number
 * @param colno - Column number
 * @param error - Error object if available
 * @returns Formatted error report
 */
export function createGlobalErrorReport(
  message: string,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error,
): ClientErrorReport {
  return {
    message,
    stack: error?.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    errorType: "global_error",
    metadata: { source, lineno, colno },
  };
}

/**
 * Create an error report from an unhandled promise rejection.
 *
 * @param reason - The rejection reason
 * @returns Formatted error report
 */
export function createRejectionReport(reason: unknown): ClientErrorReport {
  const message = reason instanceof Error ? reason.message : String(reason);
  const stack = reason instanceof Error ? reason.stack : undefined;

  return {
    message,
    stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    errorType: "unhandled_rejection",
  };
}
